// Lightweight toast (no library) — for status banners/alerts/confirms
import { useEffect, useState, useCallback, createContext, useContext } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((t) => {
    const id = Math.random().toString(36).slice(2);
    const dur = t.duration ?? 4000;
    setToasts((arr) => [...arr, { id, ...t }]);
    if (dur > 0) setTimeout(() => setToasts((arr) => arr.filter((x) => x.id !== id)), dur);
    return id;
  }, []);

  const remove = useCallback((id) => {
    setToasts((arr) => arr.filter((x) => x.id !== id));
  }, []);

  const value = {
    success: (msg) => push({ type: "success", message: msg }),
    error: (msg) => push({ type: "error", message: msg, duration: 6000 }),
    info: (msg) => push({ type: "info", message: msg }),
  };

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div style={{
        position: "fixed", top: 12, left: 0, right: 0,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
        zIndex: 200, pointerEvents: "none",
      }}>
        {toasts.map((t) => (
          <ToastBox key={t.id} toast={t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

function ToastBox({ toast, onClose }) {
  const meta = {
    success: { icon: <CheckCircle2 size={20} color="var(--success)" />, border: "rgba(16,185,129,0.5)", bg: "rgba(16,185,129,0.12)" },
    error: { icon: <AlertCircle size={20} color="var(--error)" />, border: "rgba(239,68,68,0.5)", bg: "rgba(239,68,68,0.12)" },
    info: { icon: <Info size={20} color="var(--primary)" />, border: "var(--peach-border-strong)", bg: "var(--peach-tint-soft)" },
  }[toast.type] || {};
  return (
    <div
      data-testid={`toast-${toast.type}`}
      className="fade-in"
      style={{
        pointerEvents: "auto",
        display: "flex", alignItems: "center", gap: 10,
        background: `linear-gradient(${meta.bg}, ${meta.bg}), var(--surface)`,
        border: `1px solid ${meta.border}`,
        padding: "10px 14px",
        borderRadius: 14,
        maxWidth: "92vw",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      {meta.icon}
      <span style={{ color: "var(--text-primary)", fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
        {toast.message}
      </span>
      <button onClick={onClose} aria-label="dismiss" style={{ marginLeft: 4 }}>
        <X size={16} color="var(--text-secondary)" />
      </button>
    </div>
  );
}

export function useToast() {
  const v = useContext(ToastCtx);
  if (!v) throw new Error("useToast must be inside ToastProvider");
  return v;
}
